import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import api from "../api/axios";

export default function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const form = event.currentTarget;
    const formData = new FormData(form);
    setSubmitting(true);
    setFormStatus(null);

    try {
      const { data } = await api.post("/contact-messages", {
        name: formData.get("name"),
        email: formData.get("email"),
        message: formData.get("message"),
      });
      form.reset();
      setFormStatus({ type: "success", text: data.message || "Your message has been sent." });
    } catch (error) {
      setFormStatus({
        type: "error",
        text: error.response?.data?.message || "We couldn't send your message. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="font-inter text-slate-800">
      <section className="relative overflow-hidden bg-slate-950 py-20 text-white md:py-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(37,99,235,0.35),_transparent_35%),radial-gradient(circle_at_bottom_right,_rgba(250,204,21,0.18),_transparent_30%)]" />
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
          <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto">
            Need help with your account or transaction? Our support team is ready to assist.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-8 text-center">
          <div className="bg-slate-50 border border-slate-200 rounded-[2rem] p-8">
            <Mail className="mx-auto text-primary mb-4" size={36} />
            <h3 className="text-lg font-semibold mb-2">Email</h3>
            <p className="text-slate-600">support@mekpay.com</p>
            <p className="text-xs text-slate-500 mt-1">For account and transaction support</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-[2rem] p-8">
            <Phone className="mx-auto text-primary mb-4" size={36} />
            <h3 className="text-lg font-semibold mb-2">Phone</h3>
            <p className="text-slate-600">+234 913 6194 905</p>
            <p className="text-xs text-slate-500 mt-1">Mon - Sat, 9:00 AM - 5:00 PM WAT</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-[2rem] p-8">
            <MapPin className="mx-auto text-primary mb-4" size={36} />
            <h3 className="text-lg font-semibold mb-2">Office</h3>
            <p className="text-slate-600">38 Solomon Okonkwo Street, Unity Estate, Egbeda, Lagos</p>
            <p className="text-xs text-slate-500 mt-1">Business and partnership inquiries</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-8 text-slate-900">Send a Message</h2>
          <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-[2rem] p-8 space-y-6">
            <div>
              <label htmlFor="contact-name" className="block text-sm font-semibold mb-2">Name</label>
              <input
                id="contact-name"
                name="name"
                type="text"
                placeholder="Your Full Name"
                autoComplete="name"
                maxLength={100}
                required
                className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary/30 outline-none"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-sm font-semibold mb-2">Email</label>
              <input
                id="contact-email"
                name="email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                maxLength={254}
                required
                className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary/30 outline-none"
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-sm font-semibold mb-2">Message</label>
              <textarea
                id="contact-message"
                name="message"
                rows="5"
                placeholder="Tell us how we can help"
                maxLength={5000}
                required
                className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary/30 outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="bg-primary text-white px-6 py-3 rounded-xl flex items-center gap-2 hover:bg-blue-700 transition disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Send size={18} /> {submitting ? "Sending..." : "Send Message"}
            </button>
            {formStatus && (
              <p role="status" aria-live="polite" className={`text-sm ${formStatus.type === "success" ? "text-emerald-700" : "text-red-600"}`}>
                {formStatus.text}
              </p>
            )}
            <p className="text-xs text-slate-500">
              We typically respond within one business day.
            </p>
          </form>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold mb-4 text-slate-900">Visit Our Office</h2>
          <p className="text-slate-600 mb-6">
            For scheduled meetings and partnership discussions, contact us to arrange a visit.
          </p>
          <iframe
            title="Map to 38 Solomon Okonkwo Street, Unity Estate, Egbeda, Lagos"
            src="https://maps.google.com/maps?q=38%20Solomon%20Okonkwo%20Street%2C%20Unity%20Estate%2C%20Egbeda%2C%20Lagos&output=embed"
            className="h-64 w-full rounded-[2rem] border border-slate-200"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </section>
    </div>
  );
}
